import classNames from 'classnames';
import { FocusEventHandler, KeyboardEventHandler, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useClickOutside from '../../../utils/useClickOutside';
import Input, { InputProps } from '../Input';
import LoadingIndicator from '../LoadingIndicator';
import './style.scss';

type InputWithSuggestionsProps<T> = InputProps & {
  isLoading: boolean;
  suggestions: { label: string; value: T }[];
  onSelect: (value: T) => void;
  clearField: () => void;
  dropdownError?: string;
};

const InputWithSuggestions = <T,>({
  onSelect,
  suggestions,
  onFocus,
  clearField,
  isLoading,
  dropdownError,
  ...props
}: InputWithSuggestionsProps<T>) => {
  const [isSuggestionsOpen, setSuggestionsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, isSuggestionsOpen, () => {
    setSuggestionsOpen(false);
    clearField();
  });

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Tab') {
      setSuggestionsOpen(false);
      clearField();
    }
    props.onKeyDown?.(e);
  };

  const handleSelect: (value: T) => void = value => {
    setSuggestionsOpen(false);
    onSelect(value);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = e => {
    setSuggestionsOpen(true);
    onFocus?.(e);
  };

  return (
    <div className="input-with-suggestions" ref={containerRef}>
      <Input {...props} onFocus={handleFocus} onKeyDown={handleKeyDown} />
      <ul
        className={classNames('input-with-suggestions__dropdown', {
          'input-with-suggestions__dropdown--visible': isSuggestionsOpen
        })}
      >
        <li className="input-with-suggestions__message" key="message">
          {isLoading ?
            <LoadingIndicator isSmall contentClassName="input-with-suggestions__loading" />
          : <FormattedMessage id={`address.suggestions.${!suggestions.length ? 'search' : 'select'}`} />}
        </li>
        {dropdownError ?
          <p className="input-with-suggestions__error">
            <FormattedMessage id={`errors.${dropdownError}`} />
          </p>
        : suggestions?.map(({ label, value }, i) => (
            <li className="input-with-suggestions__suggestion" key={i} onClick={() => handleSelect(value)}>
              {label}
            </li>
          ))
        }
      </ul>
    </div>
  );
};

export default InputWithSuggestions;
